import React, { useEffect, useState } from 'react';
import ClayForm, { ClayInput, ClayRadio, ClayRadioGroup } from '@clayui/form';
import ClayDatePicker from '@clayui/date-picker';
import {
    parse,
    isBefore,
    isFuture,
    isValid,
    isAfter,
    isToday,
    isSameDay,
    startOfToday
} from 'date-fns';
import {
    UseFormRegister,
    FieldValues,
    FieldErrors,
    Control,
    Controller,
    UseFormWatch,
    UseFormSetError,
    UseFormClearErrors
} from 'react-hook-form';
import './FormComponent.css';
import CustomSelect from '../customSelect/CustomSelect';
import CustomMultiSelect from '../customMultiSelect/CustomMultiSelect';
import { DATE_FORMAT, disallowedEmailDomains } from '../../utils/Config';
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import ClayLayout from '@clayui/layout';
import { FileData, UploadedDocument } from '../../interface/interface';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
interface FormComponentProps {
    onChange?: (value: string, name: string) => void; // New prop for handling changes
    control: Control<FieldValues, any>;
    label?: string;
    type: string;
    name: string;
    placeholder: string;
    register: UseFormRegister<FieldValues>;
    errors: FieldErrors<FieldValues>;
    required?: boolean;
    disabled?: boolean;
    defaultValue?: any;
    watch: UseFormWatch<FieldValues>;
    setError: UseFormSetError<FieldValues>;
    className?: string;
    size?: number;
    dateFormat?: string;
    errorTitle?: string;
    readOnly?: boolean;
    isViewOnly?: boolean;
    getValues?: (value: string) => void;
    clearErrors?: UseFormClearErrors<FieldValues>;
    setIsEditButtonDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
    setIsEditButtonDisabled2?: React.Dispatch<React.SetStateAction<boolean>>;
    setIsEditButtonDisabled3?: React.Dispatch<React.SetStateAction<boolean>>;
    setIsEditButtonDisabled4?: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedFiles?: React.Dispatch<React.SetStateAction<File[]>>;
    selectedFiles?: any;
    multiple?: boolean;
    component?:
        | 'input'
        | 'textarea'
        | React.ForwardRefExoticComponent<any>
        | undefined;
    options?: {
        key: string;
        name: string;
        organization: string;
    }[]; // Keeping the original options type
    desc?: string;
    isCurrencyInput?: boolean;
    initialCurrencyCode?: string; // Add new prop
    onCurrencyChange?: (currency: string) => void; // Add new prop
}

const FormComponent: React.FC<FormComponentProps> = ({
    onChange,
    label,
    type,
    name,
    placeholder,
    register,
    errors,
    control,
    required,
    disabled,
    className,
    defaultValue,
    readOnly,
    isViewOnly,
    options,
    errorTitle,
    dateFormat = DATE_FORMAT,
    component,
    watch,
    getValues,
    setIsEditButtonDisabled,
    setIsEditButtonDisabled2,
    setIsEditButtonDisabled3,
    setIsEditButtonDisabled4,
    setSelectedFiles,
    selectedFiles,
    clearErrors,
    desc,
    multiple,
    isCurrencyInput = false,
    initialCurrencyCode = '$', // Set default to '$'
    onCurrencyChange // Destructure new prop
}) => {
    const [expanded, setExpanded] = useState(false);
    const [isValidDate, setIsValidDate] = useState('');
    const [dateError, setDateError] = useState({
        eventDate: '',
        startDate: '',
        endDate: '',
        eventToDate: ''
    });
    const [error, setError] = useState<string>('');
    const startDate = watch('registrationStartDate');
    const eventDate = watch('eventFromDate');
    const eventToDate = watch('eventToDate');
    const endDate = watch('registrationEndDate');
    const supportedFileTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'application/pdf',
        'text/html'
    ];
    const participationAgreementTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const supportedExcelFileExtensions = [
        '.xlsx',
        '.xls',
        '.csv',
        'application/pdf'
    ];

    const supportedExcelMimeTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
    ];

    const isUploadedDocument = (file: unknown): file is UploadedDocument => {
        return (
            typeof file === 'object' &&
            file !== null &&
            typeof (file as any).objectEntryId === 'number'
        );
    };
    const inputWatch = watch(name);
    const basicDetail = useSelector((state: RootState) => state.common);
    const route = basicDetail.liferayRoute;
    // const [currency, setCurrency] = useState(initialCurrencyCode); // Remove this line
    const [formattedValue, setFormattedValue] = useState(defaultValue || '');

    useEffect(() => {
        setFormattedValue(defaultValue || '');
    }, [defaultValue]);

    const handleCurrencyChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        onCurrencyChange && onCurrencyChange(event.target.value); // Use onCurrencyChange prop
    };

    const currencyOptions = [
        { code: '$', label: 'USD' },
        { code: '€', label: 'EUR' },
        { code: 'د.إ', label: 'AED' }
        // Add other currencies as needed
    ];

    const convertToCurrency = (amount: number, currencyCode: string) => {
        if (!isNaN(amount)) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currencyCode,
                minimumFractionDigits: 0, // Ensure no decimal places for currency format
                maximumFractionDigits: 2 // Allow up to 2 decimal places
            }).format(amount);
        }
        return '';
    };

    const formatValue = (value: string | number) => {
        if (value) {
            const numericValue = parseFloat(value.toString().replace(/,/g, ''));
            if (isNaN(numericValue) || value === '') {
                return value;
            }
            // No currency symbol, just comma-separated number with decimals
            return numericValue.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            });
        }
        return '';
    };

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event?.target?.files;
        if (files && files.length > 0) {
            if (files.length > 1) {
                setError('You can only upload one file at a time.');
                return false;
            }
            const file = files[0];
            let errorMessage = '';

            // Check file size first (applies to all files)
            if (file.size > MAX_FILE_SIZE) {
                errorMessage = `Max allowed size is ${
                    MAX_FILE_SIZE / (1024 * 1024)
                } MB. File "${file.name}" exceeds the limit.`;
            }
            // Custom type restriction for Participation Agreement
            else if (name === 'participationAgreementDocument') {
                if (!participationAgreementTypes.includes(file.type)) {
                    errorMessage =
                        'Selected file type is not supported. Please select PDF or DOC/DOCX files.';
                }
            } else if (!supportedFileTypes.includes(file.type)) {
                errorMessage =
                    'Selected file type is not supported. Please select JPEG, JPG, PDF, or PNG files.';
            }

            if (errorMessage) {
                setError(errorMessage);
                return false;
            } else {
                setError('');
                if (setSelectedFiles) {
                    setSelectedFiles([file]); // Only one file is stored, replacing any previous selections
                }
                return true;
            }
        }
    };

    const handleMultipleFileSelect = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event?.target?.files;

        if (files && files.length > 0) {
            const selectedFilesArray = Array.from(files);
            let errorMessage = '';
            for (const file of selectedFilesArray) {
                const fileExtension = `.${
                    file.name.split('.').pop()?.toLowerCase() || ''
                }`;

                if (
                    name !== 'uploadDelegationFile' &&
                    !supportedFileTypes.includes(file.type)
                ) {
                    errorMessage =
                        'Selected file type is not supported. Please select JPEG, JPG, PDF, or PNG files.';
                    break;
                }

                if (
                    name === 'uploadDelegationFile' &&
                    !supportedExcelMimeTypes.includes(file.type) &&
                    !supportedExcelFileExtensions.includes(fileExtension)
                ) {
                    errorMessage =
                        'Selected file type is not supported. Please select PDF, XLSX, XLS, or CSV files.';
                    break;
                }

                if (file.size > MAX_FILE_SIZE) {
                    errorMessage = `Max allowed size is ${
                        MAX_FILE_SIZE / (1024 * 1024)
                    } MB. File "${file.name}" exceeds the limit.`;
                    break;
                }
            }

            if (errorMessage) {
                setError(errorMessage);
                return false;
            } else {
                setError('');
                if (setSelectedFiles) {
                    const uploadedFile = [
                        ...selectedFiles,
                        ...selectedFilesArray
                    ];
                    setSelectedFiles(uploadedFile);
                    // setSelectedFiles(selectedFilesArray); // Store all selected files
                }
                return true;
            }
        }
    };

    const handleRemoveFile = (fileName: string) => {
        setSelectedFiles &&
            setSelectedFiles((prevFiles) => {
                const updatedFiles = prevFiles.filter(
                    (file) => file.name !== fileName
                );

                return updatedFiles;
            });
        setError('');
    };

    useEffect(() => {
        if (startDate || eventDate || endDate || eventToDate) {
            const eventDateObj = parse(eventDate, 'dd/MM/yyyy', new Date());
            const startDateObj = parse(startDate, 'dd/MM/yyyy', new Date());
            const endDateObj = parse(endDate, 'dd/MM/yyyy', new Date());
            const eventToDateObj = parse(eventToDate, 'dd/MM/yyyy', new Date());
            const dateFormat = /^\d{2}\/\d{2}\/\d{4}$/;

            const error = {
                eventDate:
                    !dateFormat.test(eventDate) || !isValid(eventDateObj)
                        ? 'Invalid date format.'
                        : !(isToday(eventDateObj) || isFuture(eventDateObj))
                          ? 'Event Date must be in the future.'
                          : '',
                startDate:
                    !dateFormat.test(startDate) || !isValid(startDateObj)
                        ? 'Invalid date format.'
                        : '',
                endDate:
                    !dateFormat.test(endDate) || !isValid(endDateObj)
                        ? 'Invalid date format.'
                        : '',
                eventToDate:
                    !dateFormat.test(eventToDate) || !isValid(eventToDateObj)
                        ? 'Invalid date format.'
                        : ''
            };

            // Additional validations
            if (startDateObj && eventDateObj) {
                error.startDate = isAfter(startDateObj, eventDateObj)
                    ? 'Start Date must not be after the Event Date.'
                    : isBefore(startDateObj, startOfToday())
                      ? 'Start Date must not be a past date.'
                      : error.startDate;
            }

            if (endDateObj && eventDateObj) {
                error.endDate = isAfter(endDateObj, eventDateObj)
                    ? 'End Date must not be after the Event Start Date.'
                    : isBefore(endDateObj, startOfToday())
                      ? 'End Date must not be a past date.'
                      : isBefore(endDateObj, startDateObj)
                        ? 'End Date must not be before the Start Date.'
                        : error.endDate;
            }

            if (eventDateObj && eventToDateObj) {
                error.eventToDate = isBefore(eventToDateObj, eventDateObj)
                    ? 'Event To Date must not be before the Event From Date.'
                    : error.eventToDate;
            }

            // ✅ Set all errors
            setDateError(error);

            // ✅ Single place to handle button disabling
            const hasAnyDateError =
                error.eventDate ||
                error.startDate ||
                error.endDate ||
                error.eventToDate;

            if (setIsEditButtonDisabled) {
                setIsEditButtonDisabled(!!hasAnyDateError);
            }

            const hasAnyDateErrorForEventToDate = error.eventToDate;

            if (setIsEditButtonDisabled2) {
                setIsEditButtonDisabled2(!!hasAnyDateErrorForEventToDate);
            }

            const hasAnyDateError2 =
                error.eventDate || error.endDate || error.eventToDate;

            if (setIsEditButtonDisabled3) {
                setIsEditButtonDisabled3(!!hasAnyDateError2);
            }

            const hasAnyDateError3 = error.eventDate || error.eventToDate;

            if (setIsEditButtonDisabled4) {
                setIsEditButtonDisabled4(!!hasAnyDateError3);
            }
        }
    }, [startDate, eventDate, endDate, eventToDate]);

    const getClassName = () => {
        let baseClass = 'custom_input book-input no-spinner';
        // Add the 'is-invalid' class if there is an error
        if (errors[name]) {
            baseClass += ' is-invalid';
        }
        // Conditionally apply the class with important styles
        if (name === 'background' || name === 'objective') {
            baseClass += ' textarea-special';
        }
        return baseClass;
    };

    const validateDate = (date: string) => {
        if (date && date.length === 10) {
            const parsedDate = parse(date, 'dd/MM/yyyy', new Date());
            const dateFormat = /^\d{2}\/\d{2}\/\d{4}$/;

            if (!dateFormat.test(date) || !isValid(parsedDate)) {
                setIsValidDate('Invalid date format.');
                setIsEditButtonDisabled?.(true);
                setIsEditButtonDisabled3?.(true);
                setIsEditButtonDisabled4?.(true);
                return false;
            }

            if (
                name === 'eventFromDate' &&
                isBefore(parsedDate, startOfToday())
            ) {
                setIsValidDate(
                    'Date cannot be in the past. Only today or future dates are allowed.'
                );
                setIsEditButtonDisabled?.(true);
                setIsEditButtonDisabled3?.(true);
                setIsEditButtonDisabled4?.(true);
                return false;
            }

            // ✅ Today or future → valid
            setIsValidDate('');
            setIsEditButtonDisabled?.(false);
            setIsEditButtonDisabled3?.(false);
            setIsEditButtonDisabled4?.(false);
            return true;
        }
        return false;
    };

    return (
        <div className={className}>
            {type === 'text' && (
                <ClayForm.Group className={className}>
                    {label && (
                        <label
                            className={
                                name === 'background' || name === 'objective'
                                    ? 'bold-heading'
                                    : ''
                            }
                        >
                            {(() => {
                                if (desc) {
                                    return (
                                        <>
                                            {label}{' '}
                                            <i
                                                className="i-font"
                                                style={{ fontSize: '14px' }}
                                            >
                                                {desc}
                                            </i>
                                        </>
                                    );
                                } else {
                                    return label;
                                }
                            })()}
                        </label>
                    )}
                    <ClayInput
                        id={name}
                        type="text"
                        component={component}
                        placeholder={placeholder}
                        {...(readOnly
                            ? { value: defaultValue, readOnly: true }
                            : {
                                  ...register(name, {
                                      required:
                                          !disabled &&
                                          required &&
                                          `${errorTitle} is required`,
                                      validate: (value) => {
                                          const val = String(
                                              value || ''
                                          ).trim();

                                          // Check if field name contains 'link', 'Link', or 'URL'
                                          const isURLField =
                                              /link|Link|URL|website/.test(
                                                  name
                                              );

                                          // URL validation pattern
                                          const urlPattern =
                                              /^(https?:\/\/)?([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;

                                          if (required) {
                                              if (!val) {
                                                  return 'Only spaces are not allowed';
                                              } else if (
                                                  isURLField &&
                                                  !urlPattern.test(value)
                                              ) {
                                                  return 'Please enter a valid URL (e.g., google.com or https://google.com)';
                                              }
                                              if (
                                                  [
                                                      'businessEmail',
                                                      'companyOwnerEmail'
                                                  ].includes(name) &&
                                                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                                      value
                                                  )
                                              ) {
                                                  return 'Invalid email address';
                                              }
                                                if (
                                                    ['businessEmail'].includes(
                                                        name
                                                    ) &&
                                                    route !== '/attraction'
                                                ) {
                                                    const domain = val
                                                        .split('@')[1]
                                                        ?.toLowerCase();
                                                    if (
                                                        disallowedEmailDomains.includes(
                                                            domain
                                                        )
                                                    ) {
                                                        return 'Please enter a valid corporate email address';
                                                    }
                                                }
                                              if (
                                                  name ==
                                                  'descriptionOfHowYourCompanyPlansToExpand'
                                              ) {
                                                  const wordCount = (
                                                      value || ''
                                                  )
                                                      .trim()
                                                      .split(/\s+/)
                                                      .filter(Boolean).length;

                                                  if (wordCount < 30) {
                                                      return 'Please enter at least 30 words';
                                                  }
                                              }
                                              return true;
                                          } else {
                                              // For non-required fields, validate URL format only if value is provided
                                              if (
                                                  isURLField &&
                                                  val &&
                                                  !urlPattern.test(value)
                                              ) {
                                                  return 'Please enter a valid URL (e.g., google.com or https://google.com)';
                                              }
                                              return true;
                                          }
                                      }
                                  })
                              })}
                        defaultValue={defaultValue}
                        autoComplete="off"
                        disabled={disabled}
                        readOnly={readOnly}
                        className={getClassName()}
                    />
                    {errors[name] && (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {errors[name]?.message as string}
                        </div>
                    )}
                </ClayForm.Group>
            )}
            {type === 'select' && (
                <CustomSelect
                    control={control}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    defaultValue={defaultValue}
                    options={options}
                    errors={errors}
                    errorTitle={errorTitle}
                />
            )}
            {type === 'multiSelect' && (
                <ClayForm.Group
                    className={name === 'view-calendar' ? 'm-0' : ''}
                >
                    {label && <label>{label}</label>}
                    <Controller
                        control={control}
                        name={name}
                        defaultValue={defaultValue}
                        rules={{
                            required: required && `${errorTitle} is required`
                        }}
                        render={({ field: { onChange, value } }) => {
                            const formattedOptions =
                                options?.map((option) =>
                                    name === 'company'
                                        ? {
                                              key: option.organization,
                                              name: option.organization
                                          }
                                        : { key: option.key, name: option.name }
                                ) || [];
                            return (
                                <CustomMultiSelect
                                    value={value || []}
                                    handleOnChange={onChange}
                                    key={label}
                                    register={register}
                                    required={false}
                                    errors={errors}
                                    options={formattedOptions}
                                    name={name}
                                    placeholder={placeholder}
                                    disabled={disabled}
                                />
                            );
                        }}
                    />
                </ClayForm.Group>
            )}
            {type === 'radio' && (
                <ClayLayout.ContentCol
                    className="select_country"
                    style={{ marginBottom: '6px' }}
                >
                    {label && <label>{label}</label>}
                    <Controller
                        name={name}
                        control={control}
                        defaultValue={name === 'briefRadio' ? 'true' : 'false'}
                        render={({ field }) => (
                            <ClayRadioGroup
                                className="d-flex"
                                inline
                                aria-disabled={disabled}
                                {...field}
                            >
                                <ClayRadio
                                    className="w-100"
                                    label={
                                        name === 'briefRadio'
                                            ? 'Attach Document'
                                            : 'Yes'
                                    }
                                    value="true"
                                    disabled={disabled}
                                />
                                <ClayRadio
                                    className="w-100"
                                    label={
                                        name === 'briefRadio'
                                            ? 'Enter Details '
                                            : 'No'
                                    }
                                    value="false"
                                    disabled={disabled}
                                />
                            </ClayRadioGroup>
                        )}
                    />
                </ClayLayout.ContentCol>
            )}
            {type === 'phone' && (
                <ClayForm.Group className={className}>
                    {label && (
                        <label htmlFor={name} style={{ fontWeight: 500 }}>
                            {label}
                        </label>
                    )}
                    <Controller
                        name={name}
                        control={control}
                        rules={{
                            validate: (value: string) => {
                                if (!value) return `${errorTitle} is required`;
                                if (!isValidPhoneNumber(value))
                                    return `${errorTitle} is not valid`;

                                return true;
                            }
                        }}
                        defaultValue={defaultValue} // Set default value
                        render={({ field }) => (
                            <PhoneInputWithCountry
                                name={name}
                                international={true}
                                control={control as any}
                                rules={{
                                    validate: (value: string) => {
                                        if (!value)
                                            return `${errorTitle} is required`;
                                        if (!isValidPhoneNumber(value))
                                            return `${errorTitle} is not valid`;

                                        return true;
                                    }
                                }}
                                className={errors[name] ? 'is-invalid' : ''}
                                numberInputProps={{
                                    className: 'form-control'
                                }}
                            />
                        )}
                    />
                    {errors[name] && (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {errors[name]?.message as string}
                        </div>
                    )}
                </ClayForm.Group>
            )}
            {type === 'number' && (
                <ClayForm.Group className={className}>
                    {label && <label>{label}</label>}
                    <ClayInput
                        id={name}
                        type="number"
                        min={1}
                        onWheel={(e) => e.currentTarget.blur()} // Prevent scrolling
                        onKeyDown={(e) => {
                            if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        placeholder={placeholder}
                        {...(readOnly
                            ? { value: defaultValue, readOnly: true }
                            : {
                                  ...register(name, {
                                      required:
                                          required &&
                                          `${errorTitle} is required`,
                                      validate: {
                                          isPositive: (value: number) =>
                                              value > 0 ||
                                              `${errorTitle} value must be 0 or greater`,
                                          isNumber: (value: string) =>
                                              /^\d+$/.test(value) ||
                                              'Only digits are allowed'
                                          //   maxDigits: (value: string) =>
                                          //       value.length <= 16 ||
                                          //       `${errorTitle} must be 16 digits or fewer`
                                      }
                                  })
                              })}
                        // defaultValue={defaultValue}
                        autoComplete="off"
                        disabled={disabled}
                        className={
                            errors[name]
                                ? 'is-invalid'
                                : ` no-spinner custom_input book-input no-spinner`
                        }
                    />
                    {errors[name] && (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {errors[name]?.message as string}
                        </div>
                    )}
                </ClayForm.Group>
            )}
            {type === 'date' && (
                <ClayForm.Group
                    className={name === 'view-calendar' ? 'm-0' : ''}
                >
                    {label && <label>{label}</label>}
                    <Controller
                        control={control}
                        name={name}
                        defaultValue={defaultValue}
                        rules={{
                            required: required && `${errorTitle} is required`
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <ClayDatePicker
                                    onChange={(e: any) => {
                                        onChange(e); // ✅ always update selected date
                                        validateDate(e); // ✅ validate separately
                                        setExpanded(false);
                                    }}
                                    value={value || defaultValue}
                                    disabled={disabled}
                                    className="book-input"
                                    style={{
                                        backgroundColor: disabled
                                            ? '#f1f2f5'
                                            : 'transparent'
                                    }}
                                    placeholder={placeholder || dateFormat}
                                    inputName={name}
                                    dateFormat={dateFormat}
                                    id={name}
                                    expanded={expanded}
                                    onExpandedChange={setExpanded}
                                    years={{
                                        end: new Date().getFullYear() + 10,
                                        start: new Date().getFullYear() - 100
                                    }}
                                    onInput={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.value =
                                            value || defaultValue || '';
                                    }}
                                    onKeyDown={(e) => e.preventDefault()} // block keyboard typing
                                />
                            );
                        }}
                    />
                    {!disabled &&
                        !isViewOnly &&
                        dateError.startDate &&
                        name === 'registrationStartDate' && (
                            <div
                                className="invalid-feedback"
                                style={{ display: 'block' }}
                            >
                                {dateError.startDate}
                            </div>
                        )}
                    {!disabled &&
                        !isViewOnly &&
                        dateError.endDate &&
                        name === 'registrationEndDate' && (
                            <div
                                className="invalid-feedback"
                                style={{ display: 'block' }}
                            >
                                {dateError.endDate}
                            </div>
                        )}
                    {!disabled &&
                        !isViewOnly &&
                        dateError.eventDate &&
                        name === 'eventDate' && (
                            <div
                                className="invalid-feedback"
                                style={{ display: 'block' }}
                            >
                                {dateError.eventDate}
                            </div>
                        )}
                    {!disabled &&
                        !isViewOnly &&
                        dateError.eventToDate &&
                        name === 'eventToDate' && (
                            <div
                                className="invalid-feedback"
                                style={{ display: 'block' }}
                            >
                                {dateError.eventToDate}
                            </div>
                        )}
                    {!disabled && isValidDate ? (
                        <div
                            className="invalid-feedback"
                            style={{ display: 'block' }}
                        >
                            {isValidDate as string}
                        </div>
                    ) : (
                        errors[name] && (
                            <div
                                className="invalid-feedback"
                                style={{ display: 'block' }}
                            >
                                {errors[name]?.message as string}
                            </div>
                        )
                    )}
                </ClayForm.Group>
            )}
            {type === 'file' && (
                <ClayForm.Group className={className}>
                    {label && (
                        <label style={{ fontWeight: 500 }}>{label}</label>
                    )}
                    <Controller
                        control={control}
                        name={name}
                        defaultValue={defaultValue}
                        rules={{
                            required: required && `${errorTitle} is required`
                        }}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <>
                                    <div className="infocus-input-file-wrapper">
                                        <i className="input-attach-icon">
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M8.462 17.1732C6.94733 17.1732 5.65833 16.6516 4.595 15.6082C3.53167 14.5642 3 13.2876 3 11.7782C3 10.2702 3.53167 8.9909 4.595 7.94024C5.65833 6.88957 6.94733 6.36457 8.462 6.36524H17.192C18.25 6.36524 19.149 6.72924 19.889 7.45724C20.629 8.18524 20.9993 9.0779 21 10.1352C21.0007 11.1926 20.6307 12.0852 19.89 12.8132C19.1493 13.5412 18.25 13.9046 17.192 13.9032H8.923C8.33633 13.9032 7.83067 13.6986 7.406 13.2892C6.98133 12.8799 6.769 12.3806 6.769 11.7912C6.769 11.2019 6.978 10.6952 7.396 10.2712C7.814 9.84724 8.323 9.63524 8.923 9.63524H16.731C16.8723 9.63524 16.991 9.68324 17.087 9.77924C17.183 9.87524 17.231 9.9939 17.231 10.1352C17.231 10.2766 17.183 10.3952 17.087 10.4912C16.991 10.5872 16.8723 10.6352 16.731 10.6352H8.923C8.60367 10.6352 8.33167 10.7446 8.107 10.9632C7.88233 11.1819 7.76967 11.4506 7.769 11.7692C7.76833 12.0879 7.881 12.3569 8.107 12.5762C8.333 12.7956 8.605 12.9049 8.923 12.9042H17.212C17.9887 12.9002 18.6473 12.6332 19.188 12.1032C19.7293 11.5726 20 10.9166 20 10.1352C20 9.3599 19.7263 8.70424 19.179 8.16824C18.6317 7.6329 17.9693 7.36524 17.192 7.36524H8.462C7.22733 7.3619 6.175 7.78957 5.305 8.64824C4.435 9.50757 4 10.5536 4 11.7862C4 13.0016 4.435 14.0349 5.305 14.8862C6.175 15.7376 7.22733 16.1666 8.462 16.1732H16.731C16.8723 16.1732 16.991 16.2212 17.087 16.3172C17.183 16.4132 17.231 16.5322 17.231 16.6742C17.231 16.8162 17.183 16.9349 17.087 17.0302C16.991 17.1256 16.8723 17.1732 16.731 17.1732H8.462Z"
                                                    fill="#003764"
                                                />
                                            </svg>
                                        </i>
                                        <input
                                            id="file-input"
                                            type="file"
                                            onChange={(e) => {
                                                onChange(e.target.value);
                                                if (
                                                    [
                                                        'attachtradeLicenseDoc',
                                                        'attachMembershipDoc',
                                                        'attachCompanyProfile',
                                                        'companyProfile'
                                                    ]?.includes(name)
                                                ) {
                                                    handleFileSelect(e);
                                                } else {
                                                    handleMultipleFileSelect(e);
                                                }
                                            }}
                                            className="input-file"
                                            disabled={disabled}
                                            multiple={multiple}
                                            accept={
                                                name ===
                                                'participationAgreementDocument'
                                                    ? '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                                    : undefined
                                            }
                                        />
                                        <label
                                            htmlFor="file-input"
                                            className="input-file-label"
                                        >
                                            Browse the document
                                        </label>
                                    </div>
                                    {errors[name] && (
                                        <div
                                            className="invalid-feedback"
                                            style={{ display: 'block' }}
                                        >
                                            {errors[name]?.message as string}
                                        </div>
                                    )}
                                    {error && (
                                        <div
                                            className="invalid-feedback"
                                            style={{ display: 'block' }}
                                        >
                                            {error}
                                        </div>
                                    )}
                                    {selectedFiles?.length > 0 && (
                                        <div className="mt-0 selected-input-files">
                                            {selectedFiles.map((file: File) => (
                                                <div
                                                    key={
                                                        isUploadedDocument(file)
                                                            ? file?.objectEntryId?.toString()
                                                            : file.name
                                                    }
                                                    className="input-file-badge"
                                                >
                                                    {isUploadedDocument(
                                                        file
                                                    ) ? (
                                                        <span
                                                            onClick={(e) => {
                                                                const modifiedUrl =
                                                                    file.href.replace(
                                                                        'download=true',
                                                                        'black'
                                                                    );
                                                                window.open(
                                                                    modifiedUrl,
                                                                    '_blank'
                                                                );
                                                            }}
                                                            style={{
                                                                cursor: 'pointer',
                                                                textDecoration:
                                                                    'underline',
                                                                color: 'white'
                                                            }}
                                                        >
                                                            {file.documentName}
                                                        </span>
                                                    ) : (
                                                        file.name
                                                    )}
                                                    <button
                                                        className="remove-input-file-btn"
                                                        onClick={() => {
                                                            handleRemoveFile(
                                                                file.name
                                                            );

                                                            onChange(
                                                                selectedFiles.filter(
                                                                    (selectedFile: {
                                                                        name: string;
                                                                    }) =>
                                                                        selectedFile.name !==
                                                                        file.name
                                                                )
                                                            );
                                                        }}
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            );
                        }}
                    />
                </ClayForm.Group>
            )}
        </div>
    );
};

export default FormComponent;
